import type { BudgetCategory, BudgetMonth } from './types'
import { quickBudgetSuggestions } from './quick-budget'
import { formatCurrency } from './format'

interface Props {
  category: BudgetCategory
  priorMonths: Array<BudgetMonth | null | undefined>
  busy: boolean
  onAssign: (newAssigned: number) => void
  onUseAsTarget: (amount: number) => void
}

/**
 * Quick-budget stats inside the goal drawer (replaces the old per-row
 * ⚡ popover). "Assign" sets the absolute assigned value; "Target"
 * prefills the goal form below with this amount.
 */
export function QuickAssignSection({ category, priorMonths, busy, onAssign, onUseAsTarget }: Props) {
  const s = quickBudgetSuggestions(category, priorMonths)
  const rows: Array<{ label: string; value: number; hint?: string; targetable: boolean }> = []
  if (s.underfunded !== null && s.underfunded > category.assigned) {
    rows.push({ label: 'Underfunded', value: s.underfunded, hint: 'Brings Available to 0', targetable: false })
  }
  if (s.budgetedLastMonth !== null) {
    rows.push({ label: 'Budgeted last month', value: s.budgetedLastMonth, targetable: true })
  }
  if (s.spentLastMonth !== null && s.spentLastMonth > 0) {
    rows.push({ label: 'Spent last month', value: s.spentLastMonth, targetable: true })
  }
  if (s.avgAssigned3mo !== null) {
    rows.push({ label: 'Average assigned (3 mo)', value: s.avgAssigned3mo, targetable: true })
  }
  if (s.avgSpent3mo !== null && s.avgSpent3mo > 0) {
    rows.push({ label: 'Average spent (3 mo)', value: s.avgSpent3mo, targetable: true })
  }
  if (rows.length === 0) return null

  return (
    <section className="border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
        Quick assign
      </h3>
      <ul className="space-y-2 text-sm">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate">{r.label}</div>
              {r.hint && <div className="text-xs text-zinc-500">{r.hint}</div>}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <span className="font-medium tabular-nums">{formatCurrency(r.value)}</span>
              <button
                type="button"
                disabled={busy}
                onClick={() => onAssign(r.value)}
                aria-label={`Assign ${formatCurrency(r.value)} to ${category.name}`}
                className="rounded-full border border-blue-600 px-2 py-0.5 text-xs font-medium text-blue-600 hover:bg-blue-600 hover:text-white disabled:opacity-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-500 dark:hover:text-white"
              >
                Assign
              </button>
              {r.targetable && (
                <button
                  type="button"
                  onClick={() => onUseAsTarget(r.value)}
                  aria-label={`Use ${formatCurrency(r.value)} as the target for ${category.name}`}
                  title="Prefill the target form below with this amount"
                  className="rounded-full border border-zinc-300 px-2 py-0.5 text-xs text-zinc-600 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  Target
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}
